import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Typography, Paper, Tabs, Tab, TextField, Button, Grid, Avatar,
  Alert, Snackbar, CircularProgress, Divider, Chip, Stack, MenuItem,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip,
  Dialog, DialogActions, DialogContent, DialogTitle,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import LockIcon from '@mui/icons-material/Lock';
import PaymentsIcon from '@mui/icons-material/Payments';
import SaveIcon from '@mui/icons-material/Save';
import DownloadIcon from '@mui/icons-material/Download';
import { db } from '../firebase';
import { updatePassword, reauthenticateWithCredential, EmailAuthProvider } from 'firebase/auth';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { useAuth } from '../context/AuthContext';
import { CLP } from '../utils/finance';
import {
  calcularLiquidacionConContratos as calcularLiquidacion,
  getHonorarioContratos,
  getHonorarioAjustes,
  getHonorarioLiquidaciones,
  updateHonorarioLiquidacion,
  uploadBoletaHonorarios,
  acceptHonorarioContrato,
} from '../services/honorarioService';

const COLORS = {
  primary: '#1A3A5C',
  teal: '#2CB8A0',
  danger: '#DC2626',
  success: '#10B981',
  amber: '#F59E0B',
};

function TabPanel({ children, value, index }) {
  return (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const UserProfile = () => {
  const { currentUser, userData } = useAuth();
  const [tab, setTab] = useState(0);
  const isProfesional = ['Médico Veterinario', 'Director Médico', 'Radiologo'].includes(userData?.role);

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Avatar sx={{ width: 64, height: 64, bgcolor: COLORS.primary, fontSize: '1.5rem' }}>
          {(userData?.name || userData?.email || '?')[0]?.toUpperCase()}
        </Avatar>
        <Box>
          <Typography variant="h4" fontWeight={800} color={COLORS.primary}>
            Mi Perfil
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {userData?.email} · {userData?.role || 'Usuario'}
          </Typography>
        </Box>
      </Box>

      <Paper sx={{ borderRadius: 3, border: '1px solid #E2E8F0' }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab icon={<PersonIcon />} iconPosition="start" label="Datos Personales" />
          <Tab icon={<LockIcon />} iconPosition="start" label="Seguridad" />
          {isProfesional && (
            <Tab icon={<PaymentsIcon />} iconPosition="start" label="Liquidación de Honorarios" />
          )}
        </Tabs>

        <TabPanel value={tab} index={0}>
          <TabDatosPersonales currentUser={currentUser} userData={userData} />
        </TabPanel>
        <TabPanel value={tab} index={1}>
          <TabSeguridad currentUser={currentUser} />
        </TabPanel>
        {isProfesional && (
          <TabPanel value={tab} index={2}>
            <TabLiquidacion userData={userData} />
          </TabPanel>
        )}
      </Paper>
    </Box>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// TAB 1: DATOS PERSONALES
// ═══════════════════════════════════════════════════════════════════════════

const TabDatosPersonales = ({ currentUser, userData }) => {
  const [form, setForm] = useState({
    name: userData?.name || '',
    phone: userData?.phone || '',
    email: userData?.email || '',
  });
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const handleSave = async () => {
    if (!currentUser?.uid) return;
    setSaving(true);
    try {
      await updateDoc(doc(db, 'users', currentUser.uid), {
        name: form.name,
        phone: form.phone,
        updatedAt: new Date(),
      });
      setSnackbar({ open: true, message: 'Perfil actualizado correctamente', severity: 'success' });
    } catch (e) {
      setSnackbar({ open: true, message: 'Error al actualizar: ' + e.message, severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Typography variant="h6" fontWeight={700} gutterBottom color={COLORS.primary}>
        Datos Personales
      </Typography>
      <Grid container spacing={2} sx={{ maxWidth: 600 }}>
        <Grid size={{ xs: 12 }}>
          <TextField
            label="Nombre completo"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            fullWidth
            required
          />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <TextField
            label="Email"
            value={form.email}
            disabled
            fullWidth
            helperText="El email no se puede cambiar directamente. Contacta al administrador."
          />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <TextField
            label="Teléfono"
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
            fullWidth
            placeholder="+56 9 1234 5678"
          />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
            onClick={handleSave}
            disabled={saving}
            sx={{ bgcolor: COLORS.primary, '&:hover': { bgcolor: '#122a47' } }}
          >
            Guardar cambios
          </Button>
        </Grid>
      </Grid>

      {userData?.role && (
        <Box sx={{ mt: 4 }}>
          <Divider sx={{ mb: 2 }} />
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Información de cuenta
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Chip label={`Rol: ${userData.role}`} color="primary" variant="outlined" />
            {userData.professionalId && (
              <Chip label="Profesional vinculado" color="success" variant="outlined" />
            )}
            {userData.branchIds?.length > 0 ? (
              <Chip label={`${userData.branchIds.length} sucursal(es) asignada(s)`} variant="outlined" />
            ) : (
              <Chip label="Todas las sucursales" variant="outlined" />
            )}
          </Stack>
        </Box>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// TAB 2: SEGURIDAD
// ═══════════════════════════════════════════════════════════════════════════

const TabSeguridad = ({ currentUser }) => {
  const [form, setForm] = useState({ current: '', nueva: '', confirmar: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const handleChangePassword = async () => {
    setError('');
    if (!form.nueva || form.nueva.length < 6) {
      setError('La nueva contraseña debe tener al menos 6 caracteres.');
      return;
    }
    if (form.nueva !== form.confirmar) {
      setError('Las contraseñas no coinciden.');
      return;
    }
    setSaving(true);
    try {
      // Reautenticar si hay contraseña actual
      if (form.current && currentUser?.email) {
        try {
          const credential = EmailAuthProvider.credential(currentUser.email, form.current);
          await reauthenticateWithCredential(currentUser, credential);
        } catch (e) {
          setError('La contraseña actual es incorrecta.');
          setSaving(false);
          return;
        }
      }
      await updatePassword(currentUser, form.nueva);
      setSnackbar({ open: true, message: 'Contraseña actualizada correctamente', severity: 'success' });
      setForm({ current: '', nueva: '', confirmar: '' });
    } catch (e) {
      setError('Error al cambiar contraseña: ' + e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Typography variant="h6" fontWeight={700} gutterBottom color={COLORS.primary}>
        Cambiar Contraseña
      </Typography>
      <Alert severity="info" sx={{ mb: 2, maxWidth: 600 }}>
        Por seguridad, ingresa tu contraseña actual si te la solicitamos. La nueva contraseña
        debe tener al menos 6 caracteres.
      </Alert>
      <Grid container spacing={2} sx={{ maxWidth: 600 }}>
        <Grid size={{ xs: 12 }}>
          <TextField
            label="Contraseña actual"
            type="password"
            value={form.current}
            onChange={(e) => setForm({ ...form, current: e.target.value })}
            fullWidth
            helperText="Obligatorio para usuarios con sesión iniciada hace tiempo"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            label="Nueva contraseña"
            type="password"
            value={form.nueva}
            onChange={(e) => setForm({ ...form, nueva: e.target.value })}
            fullWidth
            required
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            label="Confirmar contraseña"
            type="password"
            value={form.confirmar}
            onChange={(e) => setForm({ ...form, confirmar: e.target.value })}
            fullWidth
            required
          />
        </Grid>
        {error && (
          <Grid size={{ xs: 12 }}>
            <Alert severity="error">{error}</Alert>
          </Grid>
        )}
        <Grid size={{ xs: 12 }}>
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <LockIcon />}
            onClick={handleChangePassword}
            disabled={saving || !form.nueva}
            sx={{ bgcolor: COLORS.primary, '&:hover': { bgcolor: '#122a47' } }}
          >
            Actualizar contraseña
          </Button>
        </Grid>
      </Grid>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// TAB 3: LIQUIDACIÓN DE HONORARIOS (solo Médico Veterinario / Director Médico)
// ═══════════════════════════════════════════════════════════════════════════

const TabLiquidacion = ({ userData }) => {
  const { currentUser } = useAuth();
  const professionalId = userData?.professionalId;

  // Contratos
  const [contratos, setContratos] = useState([]);
  const [loadingContratos, setLoadingContratos] = useState(true);
  const [aceptando, setAceptando] = useState('');
  const [expandedContract, setExpandedContract] = useState(null);

  useEffect(() => {
    if (!professionalId) return;
    const load = async () => {
      setLoadingContratos(true);
      try {
        const data = await getHonorarioContratos(professionalId);
        setContratos(data);
      } catch (e) {
        console.error('Error cargando contratos:', e);
      } finally {
        setLoadingContratos(false);
      }
    };
    load();
  }, [professionalId]);

  const handleAceptar = async (id) => {
    setAceptando(id);
    try {
      await acceptHonorarioContrato(id, currentUser?.uid || '');
      setContratos((prev) => prev.map((c) => (c.id === id ? { ...c, status: 'vigente' } : c)));
      setSnackbar({ open: true, message: 'Contrato aceptado. Ahora está vigente para liquidación.', severity: 'success' });
    } catch (e) {
      setSnackbar({ open: true, message: 'Error al aceptar contrato: ' + e.message, severity: 'error' });
    } finally {
      setAceptando('');
    }
  };

  const toggleExpand = (id) => setExpandedContract((prev) => (prev === id ? null : id));

  // Período
  const today = new Date();
  const [periodo, setPeriodo] = useState({
    tipo: 'mensual',
    mes: today.getMonth(),
    anio: today.getFullYear(),
    desde: '',
    hasta: '',
  });
  const [liquidacion, setLiquidacion] = useState({ items: [], resumen: null });
  const [calculando, setCalculando] = useState(false);
  const [boletaDialogOpen, setBoletaDialogOpen] = useState(false);
  const [boletaFile, setBoletaFile] = useState(null);
  const [subiendoBoleta, setSubiendoBoleta] = useState(false);
  const [historial, setHistorial] = useState([]);
  const [loadingHistorial, setLoadingHistorial] = useState(false);

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Calcular fechas del período
  const fechas = useMemo(() => {
    if (periodo.tipo === 'rango' && periodo.desde && periodo.hasta) {
      return { desde: periodo.desde, hasta: periodo.hasta, periodo: '' };
    }
    // Mensual
    const mes = Number(periodo.mes);
    const anio = Number(periodo.anio);
    const desde = new Date(anio, mes, 1);
    const hasta = new Date(anio, mes + 1, 0);
    const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    return { desde: fmt(desde), hasta: fmt(hasta), periodo: `${anio}-${String(mes + 1).padStart(2, '0')}` };
  }, [periodo]);

  // Calcular liquidación
  const handleCalcular = async () => {
    if (!professionalId) return;
    setCalculando(true);
    try {
      const guardadas = await getHonorarioLiquidaciones(professionalId, fechas.periodo);
      if (guardadas.length > 0) {
        setLiquidacion({ ...guardadas[0], esGuardada: true });
      } else {
        const ajustes = await getHonorarioAjustes(professionalId, fechas.desde, fechas.hasta);
        const result = await calcularLiquidacion(professionalId, fechas.desde, fechas.hasta, ajustes);
        setLiquidacion({ ...result, esGuardada: false });
      }
    } catch (e) {
      setSnackbar({ open: true, message: 'Error al calcular: ' + e.message, severity: 'error' });
    } finally {
      setCalculando(false);
    }
  };

  // Auto-calcular al cambiar período
  useEffect(() => {
    if (professionalId && fechas.desde && fechas.hasta) {
      handleCalcular();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [professionalId, fechas.desde, fechas.hasta]);

  // Cargar historial de liquidaciones pagadas
  const loadHistorial = async () => {
    if (!professionalId) return;
    setLoadingHistorial(true);
    try {
      const data = await getHonorarioLiquidaciones(professionalId);
      setHistorial(data.filter((l) => l.estado === 'pagada'));
    } catch (e) {
      console.error('Error cargando historial:', e);
    } finally {
      setLoadingHistorial(false);
    }
  };

  useEffect(() => {
    loadHistorial();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [professionalId]);

  const handleAprobarLiquidacion = async () => {
    if (!boletaFile || !liquidacion.id) return;
    setSubiendoBoleta(true);
    try {
      const boleta = await uploadBoletaHonorarios(liquidacion.id, boletaFile);
      await updateHonorarioLiquidacion(liquidacion.id, {
        estado: 'aprobada_por_medico',
        approvedByMedico: currentUser?.uid || '',
        approvedAtMedico: serverTimestamp(),
        boletaHonorarios: boleta,
      });
      setBoletaDialogOpen(false);
      setBoletaFile(null);
      setSnackbar({ open: true, message: 'Boleta adjuntada y liquidación aprobada', severity: 'success' });
      await handleCalcular();
    } catch (e) {
      setSnackbar({ open: true, message: 'Error al subir boleta: ' + e.message, severity: 'error' });
    } finally {
      setSubiendoBoleta(false);
    }
  };

  const abrirDialogoBoleta = () => {
    setBoletaFile(null);
    setBoletaDialogOpen(true);
  };

  // Exportar CSV
  const handleExportCSV = () => {
    const rows = liquidacion.items.map((i) => ({
      Fecha: i.fecha, Hora: i.time, Paciente: i.paciente,
      'N° Presupuesto': i.presupuestoNumber || '',
      Prestación: i.prestacion || i.tipoPrestacionName,
      'Monto Cobrado': i.montoCobrado,
      Modalidad: i.modalidad === 'porcentaje' ? `${i.porcentaje}%` : i.modalidad,
      'Monto Fijo': i.montoFijo,
      Honorario: i.honorario,
      'Estado Presupuesto': i.pagoConfirmado ? 'Pagado' : 'Pendiente',
      Boleta: i.boleta,
      Contrato: i.contratoId || '',
    }));
    if (!rows.length) return;
    const headers = Object.keys(rows[0]);
    const csv = [
      headers.join(','),
      ...rows.map((r) => headers.map((h) => `"${String(r[h] || '').replace(/"/g, '""')}"`).join(',')),
    ].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `liquidacion_honorarios_${fechas.desde}_${fechas.hasta}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!professionalId) {
    return (
      <Alert severity="warning">
        Tu usuario no tiene un profesional vinculado. Contacta al administrador para asignar tu
        ficha de profesional y acceder a la liquidación de honorarios.
      </Alert>
    );
  }

  const resumen = liquidacion.resumen;

  const contratosPendientes = contratos.filter((c) => c.status === 'pendiente');
  const contratosVigentes = contratos.filter((c) => c.status === 'vigente');

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 3 }}>
        La configuración de honorarios se gestiona desde el menú <strong>Administración &gt; Honorarios Médicos</strong>.
      </Alert>

      {/* ── Contratos del profesional ──────────────────────────────────── */}
      <Typography variant="h6" fontWeight={700} gutterBottom color={COLORS.primary}>
        Mis contratos de honorarios
      </Typography>

      {loadingContratos ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size={24} /></Box>
      ) : (
        <>
          {contratosPendientes.length > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Tienes {contratosPendientes.length} contrato(s) pendiente(s) de aceptación.
              Al aceptarlos, quedarán vigentes para el cálculo de honorarios.
            </Alert>
          )}

          {contratosPendientes.map((c) => (
            <Paper key={c.id} variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Box>
                  <Typography variant="subtitle1" fontWeight={700}>
                    {c.branchName} · {c.clinicName} · {c.specialtyName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Vigente desde {c.validFrom || '—'} {c.validTo ? `hasta ${c.validTo}` : ''}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {c.services?.length || 0} servicios configurados
                  </Typography>
                </Box>
                <Stack direction="row" spacing={1}>
                  <Button
                    variant="outlined"
                    onClick={() => toggleExpand(c.id)}
                  >
                    {expandedContract === c.id ? 'Ocultar' : 'Ver contrato'}
                  </Button>
                  <Button
                    variant="contained"
                    onClick={() => handleAceptar(c.id)}
                    disabled={aceptando === c.id}
                    startIcon={aceptando === c.id ? <CircularProgress size={16} color="inherit" /> : null}
                    sx={{ bgcolor: COLORS.primary, '&:hover': { bgcolor: '#122a47' } }}
                  >
                    Aceptar contrato
                  </Button>
                </Stack>
              </Box>

              {expandedContract === c.id && (
                <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #E2E8F0' }}>
                  {c.horarioContratado && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" fontWeight={700} gutterBottom>
                        Horario contratado
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {(() => {
                          const h = c.horarioContratado;
                          const modMap = {
                            fijo: 'Horario fijo (todas las semanas)',
                            semana_por_medio: 'Semana por medio',
                            una_vez_al_mes: 'Una vez al mes',
                            libre: 'Sin horario fijo (a demanda)',
                          };
                          const mod = modMap[h.modalidad] || h.modalidad;
                          if (h.modalidad === 'libre') return mod;
                          const diaMap = { lun: 'Lunes', mar: 'Martes', mie: 'Miércoles', jue: 'Jueves', vie: 'Viernes', sab: 'Sábado', dom: 'Domingo' };
                          const dias = (h.dias || []).map((d) => diaMap[d] || d).join(', ');
                          const horario = h.horaInicio && h.horaFin ? `${h.horaInicio} a ${h.horaFin}` : 'sin horario definido';
                          return `${mod} · ${dias || 'sin días'} · ${horario}`;
                        })()}
                      </Typography>
                      {c.horarioContratado?.montoFijoPorAsistencia > 0 && (
                        <Typography variant="body2" color="text.secondary">
                          Monto fijo por asistencia: <strong>{CLP(c.horarioContratado.montoFijoPorAsistencia)}</strong>
                        </Typography>
                      )}
                    </Box>
                  )}

                  <Typography variant="body2" fontWeight={700} gutterBottom>
                    Servicios incluidos
                  </Typography>
                  <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: 'grey.50' }}>
                          <TableCell><strong>Prestación</strong></TableCell>
                          <TableCell><strong>Modalidad</strong></TableCell>
                          <TableCell align="right"><strong>%</strong></TableCell>
                          <TableCell align="right"><strong>Monto fijo</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(c.services || []).map((s, idx) => (
                          <TableRow key={idx}>
                            <TableCell>{s.prestacion}</TableCell>
                            <TableCell>
                              <Chip label={s.modalidad} size="small" color="primary" variant="outlined" />
                            </TableCell>
                            <TableCell align="right">{s.modalidad === 'fijo' ? '—' : `${s.porcentaje}%`}</TableCell>
                            <TableCell align="right">{s.modalidad === 'porcentaje' ? '—' : CLP(s.montoFijo)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}
            </Paper>
          ))}

          {contratosVigentes.length > 0 && (
            <Paper variant="outlined" sx={{ p: 2, mb: 3, borderRadius: 2 }}>
              <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                Contratos vigentes
              </Typography>
              <Stack spacing={1}>
                {contratosVigentes.map((c) => (
                  <Box key={c.id}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                      <Box>
                        <Typography variant="body2">
                          <strong>{c.branchName} · {c.clinicName} · {c.specialtyName}</strong>
                        </Typography>
                        <Typography variant="caption" color="text.secondary" component="div">
                          {c.services?.length || 0} servicios · {c.validFrom} {c.validTo ? `→ ${c.validTo}` : ''}
                        </Typography>
                      </Box>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => toggleExpand(c.id)}
                      >
                        {expandedContract === c.id ? 'Ocultar' : 'Ver contrato'}
                      </Button>
                    </Box>

                    {expandedContract === c.id && (
                      <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #E2E8F0' }}>
                        {c.horarioContratado && (
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="body2" fontWeight={700} gutterBottom>
                              Horario contratado
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {(() => {
                                const h = c.horarioContratado;
                                const modMap = {
                                  fijo: 'Horario fijo (todas las semanas)',
                                  semana_por_medio: 'Semana por medio',
                                  una_vez_al_mes: 'Una vez al mes',
                                  libre: 'Sin horario fijo (a demanda)',
                                };
                                const mod = modMap[h.modalidad] || h.modalidad;
                                if (h.modalidad === 'libre') return mod;
                                const diaMap = { lun: 'Lunes', mar: 'Martes', mie: 'Miércoles', jue: 'Jueves', vie: 'Viernes', sab: 'Sábado', dom: 'Domingo' };
                                const dias = (h.dias || []).map((d) => diaMap[d] || d).join(', ');
                                const horario = h.horaInicio && h.horaFin ? `${h.horaInicio} a ${h.horaFin}` : 'sin horario definido';
                                return `${mod} · ${dias || 'sin días'} · ${horario}`;
                              })()}
                            </Typography>
                            {c.horarioContratado?.montoFijoPorAsistencia > 0 && (
                              <Typography variant="body2" color="text.secondary">
                                Monto fijo por asistencia: <strong>{CLP(c.horarioContratado.montoFijoPorAsistencia)}</strong>
                              </Typography>
                            )}
                          </Box>
                        )}

                        <Typography variant="body2" fontWeight={700} gutterBottom>
                          Servicios incluidos
                        </Typography>
                        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                          <Table size="small">
                            <TableHead>
                              <TableRow sx={{ bgcolor: 'grey.50' }}>
                                <TableCell><strong>Prestación</strong></TableCell>
                                <TableCell><strong>Modalidad</strong></TableCell>
                                <TableCell align="right"><strong>%</strong></TableCell>
                                <TableCell align="right"><strong>Monto fijo</strong></TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {(c.services || []).map((s, idx) => (
                                <TableRow key={idx}>
                                  <TableCell>{s.prestacion}</TableCell>
                                  <TableCell>
                                    <Chip label={s.modalidad} size="small" color="primary" variant="outlined" />
                                  </TableCell>
                                  <TableCell align="right">{s.modalidad === 'fijo' ? '—' : `${s.porcentaje}%`}</TableCell>
                                  <TableCell align="right">{s.modalidad === 'porcentaje' ? '—' : CLP(s.montoFijo)}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Box>
                    )}
                  </Box>
                ))}
              </Stack>
            </Paper>
          )}

          {contratos.length === 0 && (
            <Typography color="text.secondary" sx={{ mb: 3 }}>
              No tienes contratos de honorarios asociados. Contacta al administrador.
            </Typography>
          )}
        </>
      )}

      <Divider sx={{ my: 3 }} />

      <Typography variant="h6" fontWeight={700} gutterBottom color={COLORS.primary}>
        Liquidación de Honorarios
      </Typography>
      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
        <TextField
          select
          size="small"
          label="Período"
          value={periodo.tipo}
          onChange={(e) => setPeriodo({ ...periodo, tipo: e.target.value })}
          sx={{ minWidth: 140 }}
        >
          <MenuItem value="mensual">Mensual</MenuItem>
          <MenuItem value="rango">Rango personalizado</MenuItem>
        </TextField>

        {periodo.tipo === 'mensual' ? (
          <>
            <TextField
              select
              size="small"
              label="Mes"
              value={periodo.mes}
              onChange={(e) => setPeriodo({ ...periodo, mes: Number(e.target.value) })}
              sx={{ minWidth: 140 }}
            >
              {['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto',
                'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'].map((m, i) => (
                <MenuItem key={i} value={i}>{m}</MenuItem>
              ))}
            </TextField>
            <TextField
              select
              size="small"
              label="Año"
              value={periodo.anio}
              onChange={(e) => setPeriodo({ ...periodo, anio: Number(e.target.value) })}
              sx={{ minWidth: 100 }}
            >
              {[today.getFullYear() - 1, today.getFullYear(), today.getFullYear() + 1].map((y) => (
                <MenuItem key={y} value={y}>{y}</MenuItem>
              ))}
            </TextField>
          </>
        ) : (
          <>
            <TextField
              size="small"
              type="date"
              label="Desde"
              value={periodo.desde}
              onChange={(e) => setPeriodo({ ...periodo, desde: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              size="small"
              type="date"
              label="Hasta"
              value={periodo.hasta}
              onChange={(e) => setPeriodo({ ...periodo, hasta: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
          </>
        )}

        <Button
          variant="contained"
          size="small"
          onClick={handleCalcular}
          disabled={calculando}
          startIcon={calculando ? <CircularProgress size={16} color="inherit" /> : <PaymentsIcon />}
          sx={{ bgcolor: COLORS.primary, '&:hover': { bgcolor: '#122a47' } }}
        >
          Calcular
        </Button>

        {liquidacion.items.length > 0 && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<DownloadIcon />}
            onClick={handleExportCSV}
          >
            Exportar CSV
          </Button>
        )}
      </Stack>

      <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
        Período: {fechas.desde} a {fechas.hasta}
      </Typography>

      {liquidacion.esGuardada && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
            <Chip
              label={{
                borrador: 'Borrador',
                aprobada_por_administrador: 'Aprobada por administrador',
                aprobada_por_medico: 'Aprobada por médico',
                pago_aprobado: 'Pago aprobado',
                pagada: 'Pagada',
              }[liquidacion.estado] || liquidacion.estado}
              color={{
                borrador: 'default',
                aprobada_por_administrador: 'info',
                aprobada_por_medico: 'primary',
                pago_aprobado: 'warning',
                pagada: 'success',
              }[liquidacion.estado] || 'default'}
            />
            {liquidacion.boletaHonorarios && (
              <Button size="small" variant="outlined" onClick={() => window.open(liquidacion.boletaHonorarios.url, '_blank')}>
                Ver boleta
              </Button>
            )}
          </Stack>
          {liquidacion.estado === 'aprobada_por_administrador' && (
            <Button variant="contained" onClick={abrirDialogoBoleta}>
              Aprobar y adjuntar boleta
            </Button>
          )}
        </Paper>
      )}

      {/* ── KPIs ─────────────────────────────────────────────────────────── */}
      {resumen && (
        <Stack direction="row" spacing={2} sx={{ mb: 2, flexWrap: 'wrap' }} useFlexGap>
          {[
            { label: 'Actos médicos', value: resumen.totalActos, color: COLORS.primary },
            { label: 'Total cobrado', value: CLP(resumen.totalCobrado), color: COLORS.teal },
            { label: 'Total honorarios', value: CLP(resumen.totalHonorarios), color: resumen.totalHonorarios > 0 ? COLORS.success : 'text.secondary', bold: true },
            { label: 'Pagos confirmados', value: resumen.pagosConfirmados, color: COLORS.amber },
            { label: 'Validados', value: resumen.actosValidados || 0, color: COLORS.success },
            { label: 'Rechazados', value: resumen.actosRechazados || 0, color: resumen.actosRechazados > 0 ? COLORS.danger : 'text.secondary' },
          ].map(({ label, value, color, bold }) => (
            <Paper key={label} elevation={0} sx={{ px: 2, py: 1, border: '1px solid #E2E8F0', borderRadius: 2, minWidth: 130 }}>
              <Typography variant="h6" fontWeight={bold ? 800 : 700} color={color}>{value}</Typography>
              <Typography variant="caption" color="text.secondary">{label}</Typography>
            </Paper>
          ))}
        </Stack>
      )}

      {/* ── Tabla de detalle ────────────────────────────────────────────── */}
      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.50' }}>
              <TableCell><strong>Fecha</strong></TableCell>
              <TableCell><strong>Paciente</strong></TableCell>
              <TableCell><strong>N° Presupuesto</strong></TableCell>
              <TableCell><strong>Prestación</strong></TableCell>
              <TableCell align="right"><strong>Monto Cobrado</strong></TableCell>
              <TableCell align="center"><strong>Modalidad</strong></TableCell>
              <TableCell align="right"><strong>Honorario</strong></TableCell>
              <TableCell align="center"><strong>Estado Presupuesto</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {calculando && (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            )}
            {!calculando && liquidacion.items.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  No hay actos médicos completados en el período seleccionado.
                </TableCell>
              </TableRow>
            )}
            {!calculando && liquidacion.items.map((item) => (
              <TableRow key={item.id} hover>
                <TableCell>{item.fecha}</TableCell>
                <TableCell>{item.paciente}</TableCell>
                <TableCell>{item.presupuestoNumber || '—'}</TableCell>
                <TableCell>{item.prestacion || item.tipoPrestacionName}</TableCell>
                <TableCell align="right">{CLP(item.montoCobrado)}</TableCell>
                <TableCell align="center">
                  <Chip
                    label={
                      item.modalidad === 'sin_regla'
                        ? 'Sin regla'
                        : item.modalidad === 'porcentaje'
                        ? `${item.porcentaje}%`
                        : item.modalidad
                    }
                    size="small"
                    color={item.modalidad === 'sin_regla' ? 'error' : 'primary'}
                    variant={item.modalidad === 'sin_regla' ? 'filled' : 'outlined'}
                    sx={{ fontSize: '0.65rem' }}
                  />
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, color: item.validado ? COLORS.success : 'text.disabled' }}>
                  {item.validado ? CLP(item.honorario) : (
                    <Tooltip title={item.observacionValidacion || 'No cumple validaciones'}>
                      <Box component="span" sx={{ color: COLORS.danger, cursor: 'help', textDecoration: 'underline dotted' }}>
                        {CLP(0)}
                      </Box>
                    </Tooltip>
                  )}
                </TableCell>
                <TableCell align="center">
                  {item.pagoConfirmado ? (
                    <Chip label="Pagado" size="small" color="success" sx={{ fontSize: '0.65rem' }} />
                  ) : (
                    <Tooltip title="Presupuesto aún no pagado">
                      <Chip label="Pendiente" size="small" color="warning" variant="outlined" sx={{ fontSize: '0.65rem' }} />
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {resumen && resumen.actosRechazados > 0 && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          Hay {resumen.actosRechazados} acto(s) rechazado(s) que no se pagan. Pasa el cursor sobre
          el honorario en $0 para ver el motivo (falta ficha SOAP o protocolo quirúrgico).
        </Alert>
      )}

      {liquidacion.errores?.length > 0 && (
        <Alert severity="error" sx={{ mt: 2 }}>
          La liquidación puede estar incompleta:
          <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
            {liquidacion.errores.map((err, i) => <li key={i}>{err}</li>)}
          </Box>
        </Alert>
      )}

      {/* ── Historial de liquidaciones pagadas ───────────────────────────── */}
      <Paper variant="outlined" sx={{ p: 2, mt: 3, borderRadius: 2 }}>
        <Typography variant="h6" fontWeight={700} gutterBottom>Historial de liquidaciones</Typography>
        {loadingHistorial ? (
          <CircularProgress size={20} />
        ) : historial.length === 0 ? (
          <Typography variant="body2" color="text.secondary">No hay liquidaciones pagadas registradas.</Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Período</TableCell>
                  <TableCell>Honorarios</TableCell>
                  <TableCell>Fecha pago</TableCell>
                  <TableCell>Boleta</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {historial.map((h) => (
                  <TableRow key={h.id}>
                    <TableCell>{h.periodo}</TableCell>
                    <TableCell>{CLP(h.resumen?.totalHonorarios ?? 0)}</TableCell>
                    <TableCell>{h.paidAt ? new Date(h.paidAt.seconds * 1000).toLocaleDateString() : '—'}</TableCell>
                    <TableCell>
                      {h.boletaHonorarios ? (
                        <Button size="small" onClick={() => window.open(h.boletaHonorarios.url, '_blank')}>Ver</Button>
                      ) : '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Dialog open={boletaDialogOpen} onClose={() => setBoletaDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Adjuntar boleta de honorarios</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Sube la boleta de honorarios para aprobar la liquidación del período {fechas.periodo}.
          </Typography>
          <TextField
            type="file"
            inputProps={{ accept: '.pdf,.jpg,.jpeg,.png' }}
            onChange={(e) => setBoletaFile(e.target.files?.[0] || null)}
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBoletaDialogOpen(false)}>Cancelar</Button>
          <Button onClick={handleAprobarLiquidacion} variant="contained" disabled={!boletaFile || subiendoBoleta}>
            {subiendoBoleta ? 'Subiendo...' : 'Aprobar y subir'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UserProfile;
